<template>
  <div class="dashboard-page">
    <PageHeader header="Dashboard" icon="mdi-view-dashboard-outline" />
    <PageContent>
      <div class="dashboard-content">
        <!-- Greeting -->
        <div class="dashboard-greeting">
          <h2>{{ greeting }}, {{ firstName }}</h2>
          <p>{{ OrgInfoStore.name }}</p>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="dashboard-loading">
          <v-skeleton-loader type="card" v-for="n in 3" :key="n" class="mb-3" />
        </div>

        <template v-else>
          <!-- Stat cards -->
          <div class="dashboard-stats">
            <StatCard
              icon="mdi-clipboard-text-multiple-outline"
              :value="totalSurveys"
              label="Active Surveys"
              icon-color="#1565C0"
              icon-bg="rgba(21,101,192,0.1)"
            />
            <StatCard
              icon="mdi-map-marker-outline"
              :value="totalPlaces"
              label="Total Places"
              icon-color="#00897B"
              icon-bg="rgba(0,137,123,0.1)"
            />
            <StatCard
              icon="mdi-check-circle-outline"
              :value="overallPercent + '%'"
              label="Avg. Completion"
              icon-color="#2E7D32"
              icon-bg="rgba(46,125,50,0.1)"
            />
          </div>

          <!-- Recent surveys with progress -->
          <div class="dashboard-section">
            <div class="dashboard-section-header">
              <h3>Recent Surveys</h3>
              <v-btn text small color="primary" @click="goToSurveys">
                View all<v-icon right small>mdi-arrow-right</v-icon>
              </v-btn>
            </div>

            <div v-if="recentSurveys.length === 0" class="dashboard-empty">
              <v-icon size="48" color="#CFD8DC">mdi-clipboard-text-outline</v-icon>
              <p>No surveys yet</p>
            </div>

            <div v-else class="dashboard-survey-list">
              <button
                v-for="survey in recentSurveys"
                :key="survey.id"
                class="dashboard-survey-row"
                @click="openSurvey(survey)"
              >
                <ProgressRing
                  :percent="Number(survey.percent)"
                  :size="48"
                  :stroke-width="5"
                  :color="ringColor(Number(survey.percent))"
                  :show-label="false"
                />
                <div class="dashboard-survey-info">
                  <span class="dashboard-survey-name">{{ survey.name }}</span>
                  <span class="dashboard-survey-pct">{{ Math.round(Number(survey.percent)) }}% complete</span>
                </div>
                <StatusChip
                  :label="Number(survey.percent) >= 100 ? 'Complete' : 'Active'"
                  :color="Number(survey.percent) >= 100 ? '#2E7D32' : '#1565C0'"
                  variant="soft"
                />
              </button>
            </div>
          </div>

          <!-- Quick actions -->
          <div class="dashboard-section" v-if="UserInfoStore.admin">
            <h3 class="dashboard-section-title">Quick Actions</h3>
            <div class="dashboard-actions">
              <button class="dashboard-action" @click="newSurvey">
                <v-icon color="#1565C0">mdi-plus-circle-outline</v-icon>
                <span>New Survey</span>
              </button>
              <button class="dashboard-action" @click="goToCompany">
                <v-icon color="#1565C0">mdi-account-group-outline</v-icon>
                <span>Manage Team</span>
              </button>
            </div>
          </div>
        </template>
      </div>
    </PageContent>
  </div>
</template>

<script>
import _ from "lodash";

import PageContent from "@/components/pages/PageContent.vue";
import PageHeader from "@/components/pages/PageHeader.vue";
import StatCard from "@/components/viz/StatCard.vue";
import ProgressRing from "@/components/viz/ProgressRing.vue";
import StatusChip from "@/components/viz/StatusChip.vue";

import SurveyService from "@/services/SurveyService";

import { NavigationStore } from "@/stores/NavigationStore.js";
import { OrgInfoStore } from "@/stores/OrgInfoStore.js";
import { SurveyStore } from "@/stores/SurveyStore.js";
import { UserInfoStore } from "@/stores/UserInfoStore.js";

export default {
  name: "DashboardPage",
  components: { PageContent, PageHeader, StatCard, ProgressRing, StatusChip },
  data() {
    return {
      loading: true,
      surveys: [],
      totalResults: 0,
      OrgInfoStore,
      UserInfoStore,
    };
  },
  computed: {
    firstName() {
      return UserInfoStore.firstName || "there";
    },
    greeting() {
      const h = new Date().getHours();
      if (h < 12) return "Good morning";
      if (h < 18) return "Good afternoon";
      return "Good evening";
    },
    totalSurveys() {
      return this.totalResults || this.surveys.length;
    },
    totalPlaces() {
      return this.surveys.reduce((sum, s) => sum + (s.placeCount || 0), 0);
    },
    overallPercent() {
      if (this.surveys.length === 0) return 0;
      const avg =
        this.surveys.reduce((sum, s) => sum + Number(s.percent || 0), 0) /
        this.surveys.length;
      return Math.round(avg);
    },
    recentSurveys() {
      return this.surveys.slice(0, 5);
    },
  },
  methods: {
    ringColor(percent) {
      if (percent >= 80) return "#2E7D32";
      if (percent >= 40) return "#1565C0";
      return "#F57C00";
    },
    async fetchSurveys() {
      this.loading = true;
      try {
        const data = await SurveyService.getSurveys(0, 50, "", "", "desc", false);
        const results = _.get(data, "results", []);
        // percent comes back 0-1; normalize to 0-100 and compute place counts
        this.surveys = results.map((s) => ({
          ...s,
          percent: Number(s.percent || 0) * 100,
          placeCount: s.places ? Object.keys(s.places).length : 0,
        }));
        this.totalResults = _.get(data, "totalResults", this.surveys.length);
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    openSurvey(survey) {
      // Defer to SurveyListPage's own openSurvey so the survey detail
      // watcher is guaranteed to be mounted. The list page picks this up
      // on mount.
      SurveyStore.pendingSurvey = survey;
      NavigationStore.toggleItem("items.surveys");
    },
    goToSurveys() {
      NavigationStore.toggleItem("items.surveys");
    },
    newSurvey() {
      NavigationStore.toggleItem("items.create");
    },
    goToCompany() {
      NavigationStore.toggleItem("items.company");
    },
  },
  mounted() {
    this.fetchSurveys();
  },
};
</script>

<style scoped>
.dashboard-page {
  height: 100%;
}

.dashboard-content {
  padding: 16px;
}

.dashboard-greeting {
  margin-bottom: 24px;
}

.dashboard-greeting h2 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #263238;
  line-height: 1.2;
}

.dashboard-greeting p {
  font-size: 0.9rem;
  color: #78909C;
  margin: 4px 0 0;
}

.dashboard-stats {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 28px;
}

.dashboard-section {
  margin-bottom: 28px;
}

.dashboard-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.dashboard-section-header h3,
.dashboard-section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #37474F;
  margin: 0 0 12px;
}

.dashboard-section-header h3 {
  margin: 0;
}

.dashboard-survey-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dashboard-survey-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #ECEFF1;
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  text-align: left;
  width: 100%;
  font-family: inherit;
}

.dashboard-survey-row:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.dashboard-survey-row:focus-visible {
  outline: 3px solid #1565C0;
  outline-offset: 2px;
}

.dashboard-survey-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.dashboard-survey-name {
  font-weight: 600;
  color: #263238;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dashboard-survey-pct {
  font-size: 0.8rem;
  color: #90A4AE;
}

.dashboard-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;
  color: #B0BEC5;
}

.dashboard-empty p {
  margin-top: 8px;
}

.dashboard-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.dashboard-action {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px;
  background: #fff;
  border: 1px solid #ECEFF1;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  color: #455A64;
}

.dashboard-action:hover {
  border-color: #1565C0;
  background: rgba(21, 101, 192, 0.04);
}

.dashboard-action:focus-visible {
  outline: 3px solid #1565C0;
  outline-offset: 2px;
}
</style>
